import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronRight } from 'lucide-react';
import { useCities } from '@/hooks/useLandingContent';
import { useNeighborhoodsByCity } from '@/hooks/useNeighborhoods';
import { NeighborhoodCard } from '@/components/area-guides/NeighborhoodCard';

export default function AreaGuides() {
  const [params, setParams] = useSearchParams();
  const { data: cities } = useCities();
  const [cityId, setCityId] = useState<string | undefined>();

  useEffect(() => {
    if (!cities?.length) return;
    const fromUrl = params.get('city');
    const found = fromUrl ? cities.find(c => c.name.toLowerCase() === fromUrl.toLowerCase()) : null;
    setCityId((found || cities[0]).id);
  }, [cities, params]);

  const city = cities?.find(c => c.id === cityId);
  const { data: neighborhoods, isLoading } = useNeighborhoodsByCity(cityId);

  return (
    <Layout>
      <SEOHead
        title="Area Guides — Neighborhoods, Prices & Amenities"
        description="Explore Ethiopian neighborhoods. Compare prices, amenities, schools and properties for sale or rent in every area."
      />
      <div className="container py-6">
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">Area Guides</span>
        </nav>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Area Guides</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Discover the best neighborhoods across Ethiopia.
            </p>
          </div>
          <div className="w-full md:w-64">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Select a city</label>
            <Select
              value={cityId}
              onValueChange={(v) => {
                setCityId(v);
                const c = cities?.find(x => x.id === v);
                if (c) setParams({ city: c.name });
              }}
            >
              <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
              <SelectContent>
                {cities?.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : neighborhoods && neighborhoods.length > 0 && city ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {neighborhoods.map((n) => (
              <NeighborhoodCard key={n.id} cityName={city.name} neighborhood={n} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-12 text-center">
            <p className="text-muted-foreground">No area guides published for {city?.name} yet. Check back soon.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
